import React, { createContext, useContext, useEffect, useRef, useCallback, useMemo } from 'react';

const RealtimeContext = createContext(null);

export function RealtimeProvider({ children }) {
  const listeners = useRef(new Set());

  useEffect(() => {
    let alive = true;
    let ws = null;
    let poll = null;
    let reconnect = null;

    const emit = (evt) => listeners.current.forEach((fn) => { try { fn(evt); } catch (e) { console.error('Realtime listener error:', e); } });
    const startPoll = () => { if (!poll) poll = setInterval(() => emit({ type: 'poll' }), 6000); };
    const stopPoll = () => { if (poll) { clearInterval(poll); poll = null; } };

    const connect = () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL || '';
        const url = base.replace(/^http/, 'ws') + '/api/ws';
        ws = new WebSocket(url);
        ws.onopen = () => { if (alive) stopPoll(); };
        ws.onmessage = (e) => { try { emit(JSON.parse(e.data)); } catch (err) { emit({ type: 'update' }); } };
        ws.onclose = () => { if (alive) { startPoll(); reconnect = setTimeout(connect, 4000); } };
        ws.onerror = () => { try { ws.close(); } catch (e) { console.error('WebSocket close error:', e); } };
      } catch (e) { console.error('WebSocket connect failed, using polling:', e); startPoll(); }
    };

    connect();
    startPoll(); // ensures sync even if WS never connects; stops once WS opens

    return () => {
      alive = false;
      stopPoll();
      if (reconnect) clearTimeout(reconnect);
      if (ws) { try { ws.close(); } catch (e) { console.error('WebSocket cleanup error:', e); } }
    };
  }, []);

  const subscribe = useCallback((fn) => { listeners.current.add(fn); return () => listeners.current.delete(fn); }, []);
  const value = useMemo(() => ({ subscribe }), [subscribe]);

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

// Call `onEvent` whenever any data change is broadcast (or on the polling tick)
export function useRealtime(onEvent) {
  const ctx = useContext(RealtimeContext);
  const ref = useRef(onEvent);
  ref.current = onEvent;
  useEffect(() => {
    if (!ctx) return undefined;
    return ctx.subscribe((evt) => { if (ref.current) ref.current(evt); });
  }, [ctx]);
}
